import { AddressCheck } from '@/components/address-check';
import { ErrorAlert } from '@/components/alerts';
import { CompactButton, StandardButton } from '@/components/buttons';
import { ContentPanel } from '@/components/content-panel';
import { CurbyAccordion } from '@/components/curby-accordion';
import { CurbyHeader } from '@/components/curby-header';
import { useCurrentUser } from '@/hooks';
import { PrimaryLayout } from '@/layouts/primary';
import { findCouncil } from '@curby/shared';
import { Accordion, Flex, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { AccordionLocation } from '../../../../backend/src/types.generated';
import { useGetUserLocationQuery } from './graphql.generated';
import styles from './styles.module.css';

export const BagScannedPage = () => {
	const navigate = useNavigate();
	const { entriesGained } = useSearch({ from: '/_app/bag-scanned' });
	const backToHome = useCallback(() => navigate({ to: '/' }), [navigate]);
	const backToScanBag = useCallback(() => navigate({ to: '/scan-bag' }), [navigate]);
	const { currentUser } = useCurrentUser();

	const { data: userLocationData } = useGetUserLocationQuery({
		variables: { id: currentUser?.id ?? '' },
		skip: !currentUser?.id,
	});

	const closeButton = useMemo(
		() => (
			<CompactButton leftSection={<IconX />} onClick={backToHome}>
				Close
			</CompactButton>
		),
		[backToHome]
	);
	const footer = useMemo(
		() => (
			<Flex direction="column" w="100%" gap="md">
				<StandardButton onClick={backToScanBag}>Scan Another Bag</StandardButton>
				<StandardButton variant="light" onClick={backToHome}>
					Done
				</StandardButton>
			</Flex>
		),
		[backToScanBag, backToHome]
	);

	const address = userLocationData?.user;

	const nearestLocation = useMemo(() => {
		return (
			<Accordion.Item value={nearestLocationValue} className={styles.accordionItem}>
				<Accordion.Control className={styles.accordionControl}>
					<Text>Your nearest drop-off location:</Text>
				</Accordion.Control>
				<Accordion.Panel>
					{address && (
						<AddressCheck
							council={address.council}
							postcode={address.postcode}
							state={address.state}
							lat={address.lat}
							lng={address.lng}
						/>
					)}
				</Accordion.Panel>
			</Accordion.Item>
		);
	}, [address]);

	return (
		<PrimaryLayout
			header={{ title: 'Bag Scanned', children: closeButton }}
			footer={{ children: footer, py: 'xl' }}
		>
			<ContentPanel gap="md" align="flex-start">
				<CurbyHeader flexProps={{ direction: 'row', align: 'flex-start', gap: 'sm', w: '100%' }}>
					{entriesGained > 0 ? (
						<>
							<Flex direction="column" className={styles.entries}>
								<Text size="md" fw={900} c="white">
									You Earned
								</Text>
								<Text size="xl" fw={900}>
									+{entriesGained} entries!
								</Text>
							</Flex>
						</>
					) : (
						<>
							<Text size="xl" fw={900} className={styles.noEntries}>
								Thanks for scanning!
							</Text>
						</>
					)}
				</CurbyHeader>

				{entriesGained <= 0 && (
					<Text size="xs" fw={900}>
						To enter the prize draw, you'll need an active subscription. Subscribe today and unlock
						your chance to win exciting prizes!
					</Text>
				)}

				{findCouncil(address?.council, address?.state, address?.postcode) && (
					<ErrorAlert title="Don't forget to put your bag into your yellow lid recycling bin!" />
				)}

				<CurbyAccordion
					title="Bag Drop Off"
					accordionLocation={AccordionLocation.BagScanned}
					additionalItems={nearestLocation}
					additionalItemsDefaultValue={nearestLocationValue}
					rootClassName={styles.accordion}
				/>
			</ContentPanel>
		</PrimaryLayout>
	);
};

const nearestLocationValue = 'nearestLocation';
